import {IconButton, Menu, MenuItem, Typography} from "@mui/material"
import CheckIcon from "@mui/icons-material/Check"
import MoreVertIcon from "@mui/icons-material/MoreVert"
import LoopIcon from "@mui/icons-material/Loop"
import styled, {keyframes} from "styled-components"
import React, {useState} from "react"

import {api, Customer} from "@indebted/api"
import {useAgent} from "@indebted/hooks/Agent"
import {useNotification} from "@indebted/components/Notification"
import {ConfirmationDialog} from "@indebted/components/ConfirmationDialog"

function CustomerMenu({customer, reload}: {customer: Customer; reload: () => void}) {
	const [anchorEl, setAnchorEl] = useState<HTMLElement | undefined | null>()
	const [MenuIcon, setMenuIcon] = useState<React.ReactNode>(<MoreVertIcon />)
	const [agentEmail] = useAgent()
	const {notification} = useNotification()
	const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false)
	const [confirmationDialogOnOKFunc, setConfirmationDialogOnOKFunc] = useState<(() => void) | undefined>(undefined)
	const [confirmationDialogDescription, setConfirmationDialogDescription] = useState("")

	const handleClick = (e: React.MouseEvent<HTMLElement>) => {
		setAnchorEl(e.currentTarget)
	}

	const handleMenuClose = () => {
		setAnchorEl(null)
	}

	const handleSnoozeItemClick = (durationID: string) => {
		handleMenuClose()
		setMenuIcon(<LoadingIcon />)
		api.customer
			.snooze(customer.ID, {
				Duration: durationID,
				AgentEmail: agentEmail,
			})
			.then(() => {
				setMenuIcon(<MoreVertIcon />)
				notification.success(`Customer snoozed successfully.`)
				setTimeout(reload, 500)
			})
			.catch((error) => {
				setMenuIcon(<MoreVertIcon />)
				notification.error(error.Message)
			})
	}

	const handleEscalateCaseItemClick = (escalationID: string) => {
		handleMenuClose()
		setMenuIcon(<LoadingIcon />)
		api.customer
			.escalateCase(customer.ID, {
				Escalation: escalationID,
				AgentEmail: agentEmail,
			})
			.then(() => {
				setMenuIcon(<MoreVertIcon />)
				notification.success(`Customer case escalated successfully.`)
				setTimeout(reload, 500)
			})
			.catch((error) => {
				setMenuIcon(<MoreVertIcon />)
				notification.error(error.Message)
			})
	}

	const handleCommsClick = (actionID: string, channel: string, labelAction: string) => {
		handleMenuClose()
		setMenuIcon(<LoadingIcon />)
		api.customer
			.changeComms(customer.ID, {
				actionID,
				channel,
				AgentEmail: agentEmail,
			})
			.then(() => {
				setMenuIcon(<MoreVertIcon />)
				notification.success(`Customer ${labelAction} successful.`)
				setTimeout(reload, 500)
			})
			.catch((error) => {
				setMenuIcon(<MoreVertIcon />)
				notification.error(error.Message)
			})
	}

	const handleDigitalCommunicationConsentClick = ({ID, Display}: {ID: string; Display: string}) => {
		handleMenuClose()
		setMenuIcon(<LoadingIcon />)
		api.customer
			.changeDigitalCommConsent(customer.ID, {
				actionID: ID,
				AgentEmail: agentEmail,
			})
			.then(() => {
				setMenuIcon(<MoreVertIcon />)
				notification.success(`Customer digital comm consent was ${Display} successfully.`)
				setTimeout(reload, 500)
			})
			.catch((error) => {
				setMenuIcon(<MoreVertIcon />)
				notification.error(error.Message)
			})
	}

	const confirmDialog = (description: string, onOK: () => void) => {
		handleMenuClose()
		setConfirmationDialogDescription(description)
		setConfirmationDialogOnOKFunc(() => onOK)
		setConfirmationDialogOpen(true)
	}

	return (
		<>
			<ConfirmationDialog
				description={confirmationDialogDescription}
				isOpen={confirmationDialogOpen}
				onCancel={() => {
					setConfirmationDialogOpen(false)
				}}
				onOK={() => {
					confirmationDialogOnOKFunc?.()
					setConfirmationDialogOpen(false)
				}}
			/>

			<IconButton disabled={!!customer.Escalation} size="small" onClick={handleClick}>
				{MenuIcon}
			</IconButton>

			<Menu
				anchorEl={anchorEl}
				open={Boolean(anchorEl)}
				anchorOrigin={{vertical: "bottom", horizontal: "right"}}
				transformOrigin={{vertical: "top", horizontal: "right"}}
				onClose={handleMenuClose}
			>
				<MenuDivider color="textSecondary" variant="caption">
					Snooze
				</MenuDivider>
				{customer.SnoozeDurations.map((duration) => (
					<MenuItem
						key={duration.ID}
						onClick={() =>
							confirmDialog(`Snooze ${duration.Display} selected. Do you confirm?`, () =>
								handleSnoozeItemClick(duration.ID),
							)
						}
					>
						{duration.Display}
					</MenuItem>
				))}
				<MenuDivider color="textSecondary" variant="caption">
					Escalation
				</MenuDivider>
				{customer.Escalations.map((escalation) => (
					<MenuItem
						key={escalation.ID}
						onClick={() =>
							confirmDialog(
								`Escalation ${escalation.Display.toLowerCase()} selected. This will close the account, with no ability to reopen. Only use this  if all criteria have been met to close the account. Do you confirm?`,
								() => handleEscalateCaseItemClick(escalation.ID),
							)
						}
					>
						{escalation.Display}
					</MenuItem>
				))}
				{customer.CommsOptions &&
					customer.CommsOptions.map((option, key) => {
						return [
							<MenuDivider key={key} color="textSecondary" variant="caption">
								{option.Label}
							</MenuDivider>,
							option.Items.map((item) => (
								<MenuItem
									key={item.ID}
									onClick={() =>
										confirmDialog(`${option.Label} ${item.Display} selected. Do you confirm?`, () =>
											handleCommsClick(option.ID, item.ID, option.Label),
										)
									}
								>
									{item.Display}
								</MenuItem>
							)),
						]
					})}
				{customer.DigitalCommunicationConsentOptions &&
					customer.DigitalCommunicationConsentOptions.map((option, key) => {
						return [
							<MenuDivider key={key} color="textSecondary" variant="caption">
								{option.Label}
							</MenuDivider>,
							option.Items.map((item) => (
								<MenuItem
									key={item.ID}
									onClick={() =>
										confirmDialog(`${option.Label} ${item.Display} selected. Do you confirm?`, () =>
											handleDigitalCommunicationConsentClick(item),
										)
									}
								>
									{item.Selected && <CheckIcon fontSize="small" />}
									{item.Display}
								</MenuItem>
							)),
						]
					})}
			</Menu>
		</>
	)
}

const loadingIconAnimations = keyframes`
	from { transform:rotate(0deg); }
    to { transform:rotate(-360deg);}
`

const LoadingIcon = styled(LoopIcon)`
	animation: ${loadingIconAnimations} 4s infinite linear;
`

const MenuDivider = styled(Typography)`
	padding-left: 6px;
`

export {CustomerMenu}
